import moment from 'moment';
import {ReactElement, ReactNode} from 'react';
import {PopperPlacementType} from '@mui/material/Popper/BasePopper.types';

export type CalendarView = 'month' | 'weeks' | 'year';

export type DayType = 'default' | 'short' | 'off';

export type Size = 'small' | 'full';

export type Day = {
    id: number;
    type: DayType;
}

export type Month = {
    id: number;
    days: Array<Day>
}

export type Year = {
    id: number;
    months: Array<Month>
}

export type InRangeResult = {
    commit: boolean;
    start: boolean;
    end: boolean;
}

export type DateRange = [moment.Moment, moment.Moment | undefined | null];

export enum RangeType {
    CurrentMonth = 'currentMonth',
    PreviousMonth = 'previousMonth',
    ThisWeek = 'thisWeek',
    LastWeek = 'lastWeek',
    Custom = 'custom',
    CurrentQuarter = 'currentQuarter',
    PreviousQuarter = 'previousQuarter',
    CurrentYear = 'currentYear',
    PreviousYear = 'previousYear'
}

type BaseProps = {
    years?: Year[];
    view: CalendarView;
    onCalendarDateClick?: (date: moment.Moment) => void;
    state?: { open: boolean, setOpen: (value: boolean) => void };
    disablePortal?: boolean;
    minDate?: moment.Moment;
    maxDate?: moment.Moment;
    small?: boolean;
    placement?: PopperPlacementType;
    allowedRangeTypes?: RangeType[];
    hidePresets?: boolean;
    variant?: 'popup' | 'sheet';
    dense?: boolean;
    disabled?: boolean;
}

export type SingleSelectionProps = BaseProps & {
    value: moment.Moment;
}

export type RangeSelectionProps = BaseProps & {
    range: boolean;
    value: DateRange;
    onRangeComplete: (range: DateRange) => void
}

export type CustomRendererProps = (SingleSelectionProps | RangeSelectionProps) & {
    renderer: ReactElement;
}

export type Props = SingleSelectionProps | RangeSelectionProps | CustomRendererProps;

export interface CalendarProps {
    years?: Year[];
    view: CalendarView;
    size: Size;
    selectedRange?: DateRange;
    previewRange?: DateRange;
    onDateClick?: (date: moment.Moment) => void;
    onDayOverEnter?: (date: moment.Moment) => void;
    onDayOverLeave?: (date: moment.Moment) => void;
    flexLayout?: boolean;
    double?: boolean;
    minDate?: moment.Moment;
    maxDate?: moment.Moment;
    disabledNavigation?: boolean;
    showHeader?: boolean;
    dense: boolean;
}

export interface MonthViewProps {
    onDayClick: (day: moment.Moment) => void;
    onDayOverEnter: (day: moment.Moment) => void;
    onDayOverLeave: (day: moment.Moment) => void;
    activeDate: moment.Moment;
    month?: Month;
    range?: moment.Moment;
    getDayIsInRange: (day: moment.Moment) => InRangeResult | undefined;
    small?: boolean;
    minDate?: moment.Moment;
    maxDate?: moment.Moment;
    dense?: boolean;
}

export interface MonthViewBodyProps {
    startBlanks: Array<MonthDayCell>;
    endBlanks: Array<MonthDayCell>;
    days: Array<MonthDayCell>;
    isRangeEnabled: boolean;
    isInRange: (day?: moment.Moment) => InRangeResult | undefined;
    onDayOver?: (day?: moment.Moment) => void;
    onDayLeave?: (day?: moment.Moment) => void;
    small: boolean;
}

export interface MonthViewRowProps {
    children: MonthDayCell[] | ReactElement[];
    header?: boolean;
    isRangeEnabled: boolean;
    isInRange: (day?: moment.Moment) => InRangeResult | undefined;
    onDayOver?: (day?: moment.Moment) => void;
    onDayLeave?: (day?: moment.Moment) => void;
    small: boolean;
}

export interface MonthViewRowCellProps {
    children: ReactNode;
    isDay: boolean;
    header?: boolean;
    isRangeEnabled: boolean;
    onOver?: () => void;
    onLeave?: () => void;
    isInRange?: () => InRangeResult | undefined
    small: boolean;
}

export interface MonthViewReducerState {
    startBlankDays: Array<MonthDayCell>;
    endBlankDays: Array<MonthDayCell>;
    days: Array<MonthDayCell>;
}

export type MonthDayCell = {
    id: number;
    content: ReactElement;
    date?: moment.Moment;
}

export enum MonthViewReducerUpdateActionType {
    RELOAD_DAYS = 'RELOAD_DAYS',
    FULL_RELOAD = 'FULL_RELOAD'
}

export type MonthViewReducerUpdateAction = {
    type: MonthViewReducerUpdateActionType;
}

export type MoveProps = 'decrease' | 'increase';

export type SwitchableProps = 'disabled' | 'full' | 'start' | 'end';

export interface ViewCommonProps {
    activeDate: moment.Moment;
    onDayClick: (date: moment.Moment) => void
    onDayOverEnter: (date: moment.Moment) => void;
    onDayOverLeave: (date: moment.Moment) => void;
    minDate?: moment.Moment;
    maxDate?: moment.Moment;
    dense?: boolean;
}


